<template>
  <ion-page class="stripe-embedded-checkout-page">
    <ion-content fullscreen class="stripe-embedded-checkout-content">
      <header class="stripe-embedded-checkout-header">
        <button type="button" class="header-back-btn" aria-label="Zurück" @click="close">
          <ion-icon :icon="chevronBackOutline" class="back-icon" />
        </button>
        <div class="stripe-embedded-checkout-title info-tooltip-wrapper">
          <span>Zahlung</span>
          <button
            type="button"
            class="info-btn"
            aria-label="Hinweis zur Zahlung"
            @click.stop="toggleStripeHint"
          >
            <ion-icon :src="infoIcon" />
          </button>
        </div>
      </header>
      <div v-if="loading" class="stripe-embedded-checkout-loading">
        <ion-spinner name="crescent" />
      </div>
      <p v-if="errorMessage" class="stripe-embedded-checkout-error">{{ errorMessage }}</p>
      <div id="stripe-embedded-checkout-root" class="stripe-embedded-checkout-root" />

      <teleport to="body">
        <transition name="info-fade">
          <div
            v-if="showStripeHint"
            class="card-info-tooltip stripe-checkout-info-tooltip"
            :style="stripeHintStyle"
            @click.stop
          >
            Die Zahlung läuft über Stripe. Das ist der Zahlungsanbieter von Ollaa.
          </div>
        </transition>
      </teleport>
    </ion-content>
  </ion-page>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, nextTick } from 'vue';
import { IonPage, IonContent, IonSpinner, IonIcon, modalController } from '@ionic/vue';
import { useFixedInfoTooltip } from '@/composables/useFixedInfoTooltip';
import { chevronBackOutline } from 'ionicons/icons';
import infoIcon from '@/assets/icons_v2/info_full_icon.svg';

const props = defineProps({
  clientSecret: { type: String, required: true },
});

const loading = ref(true);
const errorMessage = ref('');
let checkoutInstance = null;

const {
  showTooltip: showStripeHint,
  tooltipStyle: stripeHintStyle,
  toggleTooltip: toggleStripeHint,
  closeTooltip: closeStripeHint,
} = useFixedInfoTooltip({
  align: 'start',
  offset: 8,
  zIndex: 40000,
  tooltipSelector: '.stripe-checkout-info-tooltip',
});

onMounted(async () => {
  const stripePublicKey = import.meta.env.VITE_STRIPE_PUBLIC_KEY;
  if (!stripePublicKey || typeof window.Stripe !== 'function') {
    errorMessage.value = 'Stripe ist nicht konfiguriert.';
    loading.value = false;
    return;
  }

  try {
    const stripe = window.Stripe(stripePublicKey);
    checkoutInstance = await stripe.initEmbeddedCheckout({
      clientSecret: props.clientSecret,
    });
    await nextTick();
    checkoutInstance.mount('#stripe-embedded-checkout-root');
  } catch (err) {
    errorMessage.value = err?.message || 'Checkout konnte nicht geladen werden.';
  } finally {
    loading.value = false;
  }
});

onBeforeUnmount(() => {
  closeStripeHint();
  if (checkoutInstance?.destroy) {
    checkoutInstance.destroy();
  }
});

const close = () => {
  closeStripeHint();
  modalController.dismiss(null, 'cancel');
};
</script>

<style scoped lang="scss">
@import '@/styles/profile-editor.scss';

.stripe-embedded-checkout-content {
  --background: #fff;
}

.stripe-embedded-checkout-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: calc(env(safe-area-inset-top, 0px) + 8px) 12px 8px;
}

.header-back-btn {
  @include modal-back-btn;
}

.back-icon {
  color: #ffcc00;
  font-size: 24px;
}

.stripe-embedded-checkout-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  font-size: 16px;
  font-weight: 600;
  color: #222;
}

.info-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  ion-icon {
    font-size: 20px;
    color: #1a1a1a;
  }
}

.stripe-embedded-checkout-loading {
  display: flex;
  justify-content: center;
  padding: 48px 0;
}

.stripe-embedded-checkout-error {
  padding: 16px;
  color: #c62828;
  text-align: center;
}

.stripe-embedded-checkout-root {
  min-height: 60vh;
}
</style>

<style lang="scss">
@use '@/styles/card-info-tooltip.scss';
</style>
